import React from 'react';
import { customerDisplayLabels } from '../lib/customerName';

/**
 * Feb 2026 — Standardised two-line customer label.
 *
 * Renders the Business Name as the **primary bold** line and the
 * Contact Person as the **secondary regular** line below. When the
 * two are identical (or one is missing) only the primary line is
 * shown — see `lib/customerName.js` for the promotion rules.
 *
 * Props
 *   customer    - the customer record.
 *   className   - wrapper class overrides (e.g., truncation).
 *   primaryClassName / secondaryClassName - typography overrides.
 *   testIdPrefix - if set, primary gets `{prefix}-primary` and
 *                  secondary gets `{prefix}-secondary`.
 *   inline       - render inline instead of stacked (used for
 *                  dropdown option items where a two-line layout
 *                  would break the row).
 */
const CustomerNameLabel = ({
  customer,
  className = '',
  primaryClassName = '',
  secondaryClassName = '',
  testIdPrefix = '',
  inline = false,
}) => {
  const { primary, secondary } = customerDisplayLabels(customer);
  if (inline) {
    return (
      <span className={className}>
        <span
          className={`font-semibold text-[#0F172A] ${primaryClassName}`}
          data-testid={testIdPrefix ? `${testIdPrefix}-primary` : undefined}
        >
          {primary}
        </span>
        {secondary && (
          <span
            className={`text-neutral-500 font-normal ml-1 ${secondaryClassName}`}
            data-testid={testIdPrefix ? `${testIdPrefix}-secondary` : undefined}
          >
            · {secondary}
          </span>
        )}
      </span>
    );
  }
  return (
    <div className={className}>
      <div
        className={`font-bold text-[#0F172A] leading-tight ${primaryClassName}`}
        data-testid={testIdPrefix ? `${testIdPrefix}-primary` : undefined}
      >
        {primary}
      </div>
      {secondary && (
        <div
          className={`text-xs text-neutral-500 font-normal leading-tight mt-0.5 ${secondaryClassName}`}
          data-testid={testIdPrefix ? `${testIdPrefix}-secondary` : undefined}
        >
          {secondary}
        </div>
      )}
    </div>
  );
};

export default CustomerNameLabel;
